import {notifyWarning} from "../components/Utils/NotificationTemplates";

export type IntegrationHost = "avid" | "premiere" | "resolve" | null;

// Läuft die App im webview eines Integrations-Plugins? window.uxpHost = Premiere ODER Resolve,
// window.mcapi = Avid.
//
// Premiere (UXP) und Resolve (Electron) sprechen ABSICHTLICH dieselbe Bridge: Das Resolve-Plugin
// stellt im webview ebenfalls ein `window.uxpHost` bereit, damit die ganze Cloud-Seite ohne
// Verdopplung für beide Hosts funktioniert. Unterschieden werden sie über `uxpHost.host` — das setzt
// nur das Resolve-Plugin, Premiere lässt es weg.
//
// In diesem Kontext sind echte Browser-Downloads (anchor.click) + Export architektonisch blockiert —
// die Aktion muss im normalen Browser laufen (Ausnahme: Premiere und Resolve laden in den Media-Ordner).
export function getIntegrationHost(): IntegrationHost {
    const w = window as any;
    if (w.uxpHost) return w.uxpHost.host === "resolve" ? "resolve" : "premiere";
    if (w.mcapi) return "avid";
    return null;
}

// Die beiden Schnitt-Hosts mit Panel-Bridge und Media-Ordner. Der Unterschied zu Avid ist nicht
// kosmetisch: dort gibt es keinen Download-Pfad, hier schon.
export function isNleHost(h: IntegrationHost): boolean {
    return h === "premiere" || h === "resolve";
}

const hostLabel = (h: IntegrationHost): string =>
    h === "premiere" ? "Premiere" : h === "resolve" ? "Resolve" : h === "avid" ? "Avid" : "";

// Produktname des laufenden Hosts, fuer jede Stelle, die ihn ANZEIGT: Modal-Titel, Tooltips,
// Ergebnis-Toasts (als `{{host}}`-Interpolation), Hinweistexte. EINE Quelle — sonst steht in der
// Resolve-Fassung an einer vergessenen Stelle weiter "Premiere".
//
// Ohne Host (normaler Browser) nennt sie beide: die Texte, in denen das auftaucht, sind gerade die,
// die erklaeren, dass es nur im Plugin geht.
export function integrationHostName(): string {
    switch (getIntegrationHost()) {
        case "premiere": return "Adobe Premiere";
        case "resolve": return "DaVinci Resolve";
        case "avid": return "Avid";
        default: return "Premiere/Resolve";
    }
}

// Toast-Hinweis, dass Download/Export im Plugin nicht geht und im Browser gemacht werden muss.
export function notifyIntegrationBlocked(action: "download" | "export", host: IntegrationHost) {
    const verb = action === "download" ? "Herunterladen" : "Exportieren";
    const what = action === "download" ? "Dateien herunterzuladen" : "zu exportieren";
    notifyWarning(`${verb} ist im ${hostLabel(host)}-Plugin nicht möglich. Bitte öffne die App im Browser, um ${what}.`);
}

// Sicherheitsnetz: Verhindert im Plugin-Kontext, dass die UXP-webview durch einen anchor-Klick von der App
// WEG-navigiert (Download-Links, fremde Origins). Passiert das, bleibt die webview auf einer Fehler-/Blank-
// Seite hängen und es gibt keinen In-webview-Reload → nur Panel-/Premiere-Neustart hilft. Same-origin
// SPA-Navigation (React Router) bleibt unberührt. Einmal beim App-Start aufrufen.
export function installIntegrationNavigationGuard() {
    if (typeof document === "undefined") return;
    document.addEventListener("click", (e) => {
        if (!getIntegrationHost()) return;
        const anchor = (e.target as HTMLElement | null)?.closest?.("a") as HTMLAnchorElement | null;
        if (!anchor) return;
        const href = anchor.getAttribute("href");
        if (!href || href.startsWith("#")) return;
        // Robuste Origin-Prüfung: ein naives startsWith("/") würde protokoll-relative "//fremd.example"
        // (und Look-alike-Hosts wie "https://app.example.com.evil.com") fälschlich als same-origin werten
        // und die webview trotzdem weg-navigieren lassen. new URL(...) löst relative + absolute korrekt auf.
        let sameOrigin = false;
        try {
            sameOrigin = new URL(href, window.location.href).origin === window.location.origin;
        } catch {
            sameOrigin = false; // unparsebare href -> defensiv als fremd behandeln (abfangen).
        }
        // download-Attribut oder fremde Origin → würde die webview weg-navigieren → im Plugin abfangen.
        if (anchor.hasAttribute("download") || !sameOrigin) {
            // Nur die Navigation verhindern — KEIN stopPropagation: dieser Listener läuft am document in der
            // Capture-Phase, React hängt seine Handler am #root; ein stopPropagation hier verschluckte einen
            // echten onClick auf so einem <a>. preventDefault allein stoppt das Weg-Navigieren.
            e.preventDefault();
        }
    }, true);
}
